<template>
  <div class="login-container">
    <div class="login-card">
      <!-- LOGO HEADER -->
      <div class="login-header" v-if="!showVerification">
        <img src="/logo192.png" alt="OKOLISY" class="logo">
        <h1>Administration OKOLISY</h1>
        <p>Connectez-vous pour accéder au tableau de bord</p>
      </div>

      <div class="login-header" v-else>
        <img src="/logo192.png" alt="OKOLISY" class="logo">
        <h1>🔐 Vérification requise</h1>
        <p>Un code de sécurité a été envoyé à <strong>okolisyadmin@gmail.com</strong></p>
        <p class="security-note">⚠️ Ce code est demandé à chaque connexion pour des raisons de sécurité</p>
      </div>

      <!-- FORMULAIRE DE CONNEXION -->
      <div v-if="!showVerification">
        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-group">
            <label for="identifier">
              <font-awesome-icon icon="envelope" />
              Email ou Téléphone
            </label>
            <input 
              type="text" 
              id="identifier" 
              v-model="form.identifier" 
              placeholder="example@gmail.com"
              required
              :disabled="loading"
            >
            <!-- placeholder="admin@okolisy.com ou 066000000" -->
          </div>

          <div class="form-group">
            <label for="password">
              <font-awesome-icon icon="lock" />
              Mot de passe
            </label>
            <div class="password-input">
              <input 
                :type="showPassword ? 'text' : 'password'" 
                id="password" 
                v-model="form.password" 
                placeholder="••••••••"
                required
                :disabled="loading"
              >
              <button 
                type="button" 
                class="toggle-password" 
                @click="showPassword = !showPassword"
              >
                <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
              </button>
            </div>
          </div>

          <div v-if="error" class="error-message">
            <font-awesome-icon icon="exclamation-circle" />
            {{ error }}
          </div>

          <button type="submit" class="login-btn" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            <span v-else>Se connecter</span>
          </button>
        </form>
      </div>

      <!-- FORMULAIRE DE VÉRIFICATION (CODE) -->
      <div v-else class="verification-form">
        <div class="verification-header">
          <p>Entrez le code à 4 chiffres reçu par email</p>
        </div>
        
        <div class="code-inputs">
          <input 
            v-for="(digit, index) in 4" 
            :key="index"
            ref="codeInputs"
            type="text"
            maxlength="1"
            class="code-digit"
            v-model="codeDigits[index]"
            @input="handleCodeInput(index, $event)"
            @keydown="handleCodeKeydown(index, $event)"
            @paste="handlePaste"
          >
        </div>
        
        <div v-if="verificationError" class="error-message" :class="{ success: verificationSuccess }">
          <font-awesome-icon :icon="verificationSuccess ? 'check-circle' : 'exclamation-circle'" />
          {{ verificationError }}
        </div>
        
        <button class="verify-btn" @click="verifyCode" :disabled="verifying">
          <span v-if="verifying" class="spinner"></span>
          <span v-else>Vérifier et se connecter</span>
        </button>
        
        <div class="resend">
          <button class="resend-btn" @click="resendCode" :disabled="resending">
            {{ resending ? 'Envoi...' : '📧 Renvoyer le code' }}
          </button>
        </div>
        
        <div class="back-to-login">
          <button class="back-btn" @click="goBackToLogin">
            ← Retour à la connexion
          </button>
        </div>
      </div>

      <div class="login-footer">
        <a href="/">Retour au site</a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useAdminAuthStore } from './stores/adminAuthStore';
import { $api } from '@/main';

export default defineComponent({
  name: 'LoginView',
  setup() {
    const router = useRouter();
    const adminAuth = useAdminAuthStore();
    
    const form = ref({
      identifier: '',
      password: ''
    });
    
    const showPassword = ref(false);
    const loading = ref(false);
    const error = ref('');
    
    // Vérification
    const showVerification = ref(false);
    const verifying = ref(false);
    const resending = ref(false);
    const verificationError = ref('');
    const verificationSuccess = ref(false);
    const pendingUserId = ref<number | null>(null);
    const codeDigits = ref(['', '', '', '']);
    const codeInputs = ref<HTMLInputElement[]>([]);
    
    const handleCodeInput = (index: number, event: Event) => {
      const input = event.target as HTMLInputElement;
      if (input.value.length === 1 && index < 3) {
        nextTick(() => {
          codeInputs.value[index + 1]?.focus();
        });
      }
    };
    
    const handleCodeKeydown = (index: number, event: KeyboardEvent) => {
      if (event.key === 'Backspace' && !codeDigits.value[index] && index > 0) {
        nextTick(() => {
          codeInputs.value[index - 1]?.focus();
        });
      }
    };
    
    const handlePaste = (event: ClipboardEvent) => {
      const pasted = event.clipboardData?.getData('text') || '';
      const digits = pasted.replace(/\D/g, '').slice(0, 4);
      if (digits) {
        for (let i = 0; i < digits.length; i++) {
          codeDigits.value[i] = digits[i];
        }
        if (digits.length === 4 && codeInputs.value[3]) {
          codeInputs.value[3].focus();
        }
      }
    };
    
    const handleLogin = async () => {
      if (!form.value.identifier || !form.value.password) {
        error.value = 'Veuillez remplir tous les champs';
        return;
      }

      loading.value = true;
      error.value = '';

      const result = await adminAuth.login(form.value.identifier, form.value.password);
      
      if (result.success) {
        router.push('/admin');
      } else if (result.requiresVerification) {
        pendingUserId.value = result.userId;
        showVerification.value = true;
        error.value = '';
        verificationError.value = '';
        verificationSuccess.value = false;
        codeDigits.value = ['', '', '', ''];
        nextTick(() => {
          codeInputs.value[0]?.focus();
        });
      } else {
        error.value = result.error || result.message || 'Erreur de connexion';
      }

      loading.value = false;
    };
    
    const verifyCode = async () => {
      const code = codeDigits.value.join('');
      if (code.length !== 4) {
        verificationError.value = 'Code incomplet - veuillez saisir 4 chiffres';
        verificationSuccess.value = false;
        return;
      }
      
      verifying.value = true;
      verificationError.value = '';
      verificationSuccess.value = false;
      
      try {
        const response = await $api.post('/api/verify-code', {
          user_id: pendingUserId.value,
          code: code
        });
        
        if (response.data.success) {
          verificationSuccess.value = true;
          verificationError.value = '✅ Connexion confirmée ! Redirection...';
          
          localStorage.setItem('admin_token', response.data.token);
          localStorage.setItem('admin_user', JSON.stringify(response.data.user));
          
          adminAuth.state.user = response.data.user;
          adminAuth.state.token = response.data.token;
          adminAuth.state.isAuthenticated = true;
          
          // Rediriger après un court délai
          setTimeout(() => {
            router.push('/admin');
          }, 800);
        } else {
          verificationError.value = response.data.message || '❌ Code invalide';
          verificationSuccess.value = false;
        }
      } catch (err: any) {
        console.error('Erreur vérification:', err);
        verificationError.value = err.response?.data?.message || '❌ Erreur de vérification';
        verificationSuccess.value = false;
      } finally {
        verifying.value = false;
      }
    };
    
    const resendCode = async () => {
      resending.value = true;
      verificationError.value = '';
      verificationSuccess.value = false;
      
      try {
        await $api.post('/api/resend-code', { user_id: pendingUserId.value });
        verificationSuccess.value = true;
        verificationError.value = '✅ Un nouveau code a été envoyé à okolisyadmin@gmail.com !';
        codeDigits.value = ['', '', '', ''];
        nextTick(() => {
          codeInputs.value[0]?.focus();
        });
        setTimeout(() => {
          verificationSuccess.value = false;
        }, 5000);
      } catch (err: any) {
        verificationError.value = '❌ Erreur lors du renvoi du code';
        verificationSuccess.value = false;
      } finally {
        resending.value = false;
      }
    };
    
    const goBackToLogin = () => {
      showVerification.value = false;
      verificationError.value = '';
      verificationSuccess.value = false;
      codeDigits.value = ['', '', '', ''];
    };

    return {
      form,
      showPassword,
      loading,
      error,
      showVerification,
      verifying,
      resending,
      verificationError,
      verificationSuccess,
      codeDigits,
      codeInputs,
      handleLogin,
      handleCodeInput,
      handleCodeKeydown,
      handlePaste,
      verifyCode,
      resendCode,
      goBackToLogin
    };
  }
});
</script>

<style lang="scss" scoped>
@use '@/styles/variables' as *;

.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $orange-doux 0%, $orange-fonce 100%);
  padding: 20px;
}

.login-card {
  background: white;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
  
  .logo {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
  }
  
  h1 {
    color: $gris-fonce;
    font-size: 1.5rem;
    margin-bottom: 10px;
  }
  
  p {
    color: $gris-moyen;
    font-size: 0.9rem;
  }
  
  .security-note {
    color: #dc3545;
    font-size: 0.8rem;
    font-weight: 500;
    margin-top: 8px;
    background: #f8d7da;
    padding: 6px 12px;
    border-radius: 6px;
  }
}

.verification-header {
  text-align: center;
  margin-bottom: 30px;
  
  p {
    color: $gris-moyen;
    font-size: 0.9rem;
  }
}

.code-inputs {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 20px;
  
  .code-digit {
    width: 60px;
    height: 70px;
    text-align: center;
    font-size: 28px;
    font-weight: bold;
    border: 2px solid #eee;
    border-radius: 12px;
    transition: border-color 0.2s;
    
    &:focus {
      outline: none;
      border-color: $orange-doux;
      box-shadow: 0 0 0 3px rgba(255, 167, 84, 0.2);
    }
  }
}

.verify-btn {
  width: 100%;
  padding: 14px;
  background: $orange-fonce;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 15px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: darken($orange-fonce, 10%);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #fff;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

.resend {
  text-align: center;
  margin-bottom: 10px;
  
  .resend-btn {
    background: none;
    border: none;
    color: $orange-doux;
    cursor: pointer;
    font-size: 0.9rem;
    
    &:hover:not(:disabled) {
      text-decoration: underline;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.back-to-login {
  text-align: center;
  
  .back-btn {
    background: none;
    border: none;
    color: $gris-moyen;
    cursor: pointer;
    font-size: 0.85rem;
    
    &:hover {
      color: $orange-doux;
    }
  }
}

.login-form {
  .form-group {
    margin-bottom: 20px;
    
    label {
      display: block;
      margin-bottom: 8px;
      color: $gris-fonce;
      font-weight: 500;
      font-size: 0.9rem;
      
      svg {
        margin-right: 5px;
        color: $orange-doux;
      }
    }
    
    input {
      width: 100%;
      padding: 12px 15px;
      border: 2px solid #eee;
      border-radius: 8px;
      font-size: 1rem;
      
      &:focus {
        outline: none;
        border-color: $orange-doux;
      }
      
      &:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
      }
    }
    
    .password-input {
      position: relative;
      
      input {
        padding-right: 45px;
      }
      
      .toggle-password {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: $gris-moyen;
        cursor: pointer;
        
        &:hover {
          color: $orange-doux;
        }
      }
    }
  }
}

.error-message {
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:not(.success) {
    background: #f8d7da;
    color: #721c24;
  }
  
  &.success {
    background: #d4edda;
    color: #155724;
  }
}

.login-btn {
  width: 100%;
  padding: 14px;
  background: $orange-fonce;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: darken($orange-fonce, 10%);
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #fff;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

.login-footer {
  text-align: center;
  margin-top: 20px;
  
  a {
    color: $gris-moyen;
    text-decoration: none;
    font-size: 0.9rem;
    
    &:hover {
      color: $orange-doux;
    }
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// Responsive
@media (max-width: 480px) {
  .login-card {
    padding: 25px;
  }
  
  .code-inputs {
    gap: 10px;
    
    .code-digit {
      width: 50px;
      height: 60px;
      font-size: 24px;
    }
  }
}
</style>